import { isEqual } from "lodash";
import { useSelector } from "react-redux";
import { DashboardTypes } from "./types";
import { useCurrentDashboardEntityId, useCurrentDashboardType } from "./use-current-dashboard";
import { useGetAssetById, useGetFundById, useGetManagerById, useGetPortfolioById } from "api/ux/src";
import { useGetFundSeriesById } from "components/pages/fund-series/api";
import { moveElementsToBeginning } from "common/array-helper";
import { getDateWhenString } from "common/date-utils";
import { useGetMarket } from "hooks/use-get-market";
import { language } from "language/default";
const getAssetSelectedDeal = (allDeals, selectedFundId) => {
    const deals = allDeals?.filter((d) => d.fundId === selectedFundId);
    let [fund] = deals || [];
    if (!fund && allDeals?.length) {
        [fund] = allDeals;
    }
    return fund;
};
export const dashboardPageTypeDefinitionsByType = {
    [DashboardTypes.Asset]: {
        availableCurrenciesSelector: (state) => {
            const assetDashboardOptions = state.currentDashboard.dashboardOptions, record = state.entities.Asset[state.currentDashboard.currentEntityId];
            const currencies = [{ currencyCode: record?.currency || "", label: `${DashboardTypes.Asset.toString()} (${record?.currency})` }];
            const fund = getAssetSelectedDeal(record?.deals, assetDashboardOptions?.selectedFundId);
            if (fund && fund.fundCurrency) {
                currencies.unshiftIfNotExist({
                    currencyCode: fund.fundCurrency, label: `${DashboardTypes.Fund.toString()} (${fund.fundCurrency})`
                }, (c) => c.currencyCode === fund.fundCurrency);
            }
            return currencies;
        },
        dashboardType: DashboardTypes.Asset,
        entityInfoSelector: (state) => {
            const record = state.entities.Asset[state.currentDashboard.currentEntityId];
            const fund = getAssetSelectedDeal(record?.deals, state.currentDashboard?.dashboardOptions?.selectedFundId);
            return {
                assetClasses: fund?.assetClass ? [fund.assetClass] : [],
                currency: record?.currency || "",
                id: state.currentDashboard.currentEntityId,
                name: record?.name || "",
                type: DashboardTypes.Asset
            };
        },
        pageLocationPathMatch: "/asset/",
        useGetTypeEntitiesData: useGetAssetById
    },
    [DashboardTypes.Fund]: {
        availableCurrenciesSelector: (state) => {
            const record = state.entities.Fund[state.currentDashboard.currentEntityId];
            const availableCurrencies = [
                {
                    currencyCode: record?.currency || "",
                    label: `${DashboardTypes.Fund.toString()} (${record?.currency})`
                }
            ];
            const fundDashboardMyPositionsOptions = state.currentDashboard.dashboardOptions;
            if (fundDashboardMyPositionsOptions?.enabled) {
                const investors = record.subscriptions?.filter((s) => s.investmentId === fundDashboardMyPositionsOptions?.selectedInvestorId);
                investors?.length === 1 && investors[0].currency !== record?.currency && availableCurrencies.push({
                    currencyCode: investors[0].currency || "",
                    label: `${language.en.funds.investor} (${investors[0].currency || ""})`
                });
            }
            return availableCurrencies;
        },
        dashboardType: DashboardTypes.Fund,
        entityInfoSelector: (state) => {
            const record = state.entities.Fund[state.currentDashboard.currentEntityId];
            const selectedInvestorId = state.currentDashboard.dashboardOptions?.selectedInvestorId;
            const subscription = selectedInvestorId ? record?.subscriptions?.find((s) => s.investmentId === selectedInvestorId) : undefined;
            return {
                assetClasses: record?.assetClass ? [record.assetClass] : [],
                currency: record?.currency || "",
                id: state.currentDashboard.currentEntityId,
                investorId: selectedInvestorId,
                investorName: subscription?.investorName,
                name: record?.name || "",
                referenceDate: getDateWhenString(record?.referenceDate),
                type: DashboardTypes.Fund
            };
        },
        pageLocationPathMatch: "/fund/",
        useGetTypeEntitiesData: useGetFundById
    },
    [DashboardTypes.FundSeries]: {
        availableCurrenciesSelector: (state) => {
            const record = state.entities.FundSeries[state.currentDashboard.currentEntityId];
            return [
                {
                    currencyCode: record?.currency || "",
                    label: `${DashboardTypes.FundSeries.toString()} (${record?.currency})`
                }
            ];
        },
        dashboardType: DashboardTypes.FundSeries,
        entityInfoSelector: (state) => {
            const record = state.entities.FundSeries[state.currentDashboard.currentEntityId];
            return {
                assetClasses: record?.assetClasses || [],
                currency: record?.currency || "",
                id: state.currentDashboard.currentEntityId,
                name: record?.name || "",
                type: DashboardTypes.FundSeries
            };
        },
        pageLocationPathMatch: "/fund-series/",
        useGetTypeEntitiesData: useGetFundSeriesById
    },
    [DashboardTypes.Manager]: {
        availableCurrenciesSelector: (state) => {
            const record = state.entities.Manager[state.currentDashboard.currentEntityId];
            return [
                {
                    currencyCode: record?.currency || "",
                    label: `${DashboardTypes.Manager.toString()} (${record?.currency})`
                }
            ];
        },
        dashboardType: DashboardTypes.Manager,
        entityInfoSelector: (state) => {
            const record = state.entities.Manager[state.currentDashboard.currentEntityId];
            return {
                assetClasses: record?.assetClasses || [],
                currency: record?.currency || "",
                id: state.currentDashboard.currentEntityId,
                name: record?.name || "",
                type: DashboardTypes.Manager
            };
        },
        pageLocationPathMatch: "/manager/",
        useGetTypeEntitiesData: useGetManagerById
    },
    [DashboardTypes.Portfolio]: {
        availableCurrenciesSelector: (state) => {
            const record = state.entities.Portfolio[state.currentDashboard.currentEntityId];
            return [
                {
                    currencyCode: record?.currency || "",
                    label: `${DashboardTypes.Portfolio.toString()} (${record?.currency})`
                }
            ];
        },
        dashboardType: DashboardTypes.Portfolio,
        entityInfoSelector: (state) => {
            const record = state.entities.Portfolio[state.currentDashboard.currentEntityId];
            return {
                assetClasses: record?.assetClasses || [],
                currency: record?.currency || "",
                id: state.currentDashboard.currentEntityId,
                name: record?.name || "",
                type: DashboardTypes.Portfolio
            };
        },
        pageLocationPathMatch: "/portfolio/",
        useGetTypeEntitiesData: useGetPortfolioById
    },
    [DashboardTypes.Market]: {
        availableCurrenciesSelector: (state) => {
            const selectedCurrency = state.entities.Market[state.currentDashboard.currentEntityId]?.currency;
            const currency = Intl.supportedValuesOf("currency");
            const currencyOrderer = moveElementsToBeginning(currency, ["CNY", "JPY", "GBP", "EUR", "USD", selectedCurrency]);
            return currencyOrderer.map((e) => ({ currencyCode: e, label: e }));
        },
        dashboardType: DashboardTypes.Market,
        entityInfoSelector: (state) => {
            const record = state.entities.Market[state.currentDashboard.currentEntityId];
            return {
                assetClasses: [],
                currency: "USD",
                id: state.currentDashboard.currentEntityId,
                isCustom: false,
                name: record?.name || "",
                subtype: record?.marketType,
                type: DashboardTypes.Market
            };
        },
        pageLocationPathMatch: "/market/",
        useGetTypeEntitiesData: useGetMarket
    }
};
export const dashboardPageDefinitionsByLocationPath = Object.entries(dashboardPageTypeDefinitionsByType)
    .reduce((prev, [, value]) => {
    prev[value.pageLocationPathMatch] = value;
    return prev;
}, {});
export const dashboardPageTypeLocationPaths = Object.keys(dashboardPageDefinitionsByLocationPath);
export const getDashboardType = (type) => (type && type in dashboardPageTypeDefinitionsByType ? type : DashboardTypes.Fund);
export const useGetEntityInfo = () => {
    const currentDashboardEntityId = useCurrentDashboardEntityId(), selectedType = useCurrentDashboardType();
    const dashboardTypeDef = dashboardPageTypeDefinitionsByType[getDashboardType(selectedType)];
    const [, queryState] = dashboardTypeDef.useGetTypeEntitiesData({ id: currentDashboardEntityId, isCustom: false });
    return {
        ...useSelector(dashboardTypeDef.entityInfoSelector, isEqual),
        queryState
    };
};
export const useGetAvailableCurrencies = () => {
    const currentDashboardEntityId = useCurrentDashboardEntityId(), selectedType = useCurrentDashboardType();
    const dashboardTypeDef = dashboardPageTypeDefinitionsByType[getDashboardType(selectedType)];
    dashboardTypeDef.useGetTypeEntitiesData({ id: currentDashboardEntityId, isCustom: false });
    return useSelector(dashboardTypeDef.availableCurrenciesSelector, isEqual);
};
